// third party
import { configureStore } from '@reduxjs/toolkit';
import {
  useDispatch as useAppDispatch,
  useSelector as useAppSelector,
  TypedUseSelectorHook,
} from 'react-redux';

import { persistStore } from 'redux-persist';

// project imports
import rootReducer from './reducer';

// ==============================|| REDUX - MAIN STORE ||============================== //

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false, immutableCheck: false }),
});
// * Use for testing
//  store.subscribe(() => {
//   const state = store.getState();
//   console.log('Store update: ', state);
// });

const persister = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;

type AppDispatch = typeof store.dispatch;

const { dispatch } = store;

function useDispatch() {
  return useAppDispatch<AppDispatch>();
}

const resetStore = () => {
  persister.purge();
  dispatch({ type: 'RESET_STORE' });
};

const useSelector: TypedUseSelectorHook<RootState> = useAppSelector;

// ==============================|| DEV ONLY - REDUX HMR ||============================== //
//* ===== FIX (2026-07-19) — עצירת "page reload" של Vite בפיתוח =====
//* בעיה: כל שינוי בקובץ util/api שמיובא ע"י אחד ה-slices (למשל utils/convert-messages.ts
//* דרך api/chat.ts) טיפס בשרשרת ה-imports: slice → reducer.ts → store → index.tsx.
//* index.tsx הוא ה-entry, ול-React Fast Refresh אין שם "גבול" לעצור בו — לכן Vite
//* ביצע רענון מלא של הדפדפן (בטרמינל: "[vite] (client) page reload ...").
//* המשתמש חווה זאת כ"המסך מתרענן לבד" בזמן פיתוח.
//*
//* פתרון: מקבלים כאן עדכוני HMR עבור מודול ה-reducer בלבד ומחליפים את ה-reducer
//* בחיים (replaceReducer) — הדפוס הרשמי של Redux + Vite. שרשרת ה-HMR נעצרת כאן,
//* אין רענון דף, וה-state הקיים נשמר.
//*
//* מגבלה ידועה (מקובלת): קוד שכבר "תפס" reference ישן מחוץ ל-reducer (למשל closure
//* שנוצר לפני העדכון) ימשיך להריץ את הגרסה הישנה עד רענון ידני — מקרה קצה נדיר.
//* אין שום השפעה על production (import.meta.hot קיים רק ב-dev, ה-build מסיר את הבלוק).
if (import.meta.hot) {
  import.meta.hot.accept('./reducer', (mod) => {
    const nextRootReducer = mod?.default;
    if (nextRootReducer) {
      store.replaceReducer(nextRootReducer);
    }
  });
}

export { store, persister, dispatch, useSelector, useDispatch, resetStore };
